import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  Paper,
  MenuItem,
  Link,
  Radio,
  Divider,
  useTheme,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";

import { imageSource, Strings } from "@app/constants";
import { Spacing } from "@app/components/spacing";
import {
  FormProvider,
  RHFRadioButton,
  RHFSelect,
  RHFTextField,
} from "@app/components";
import { MapModal } from "./map-modal";
import { addProduct, brands, category } from "../slice";
import { LoadingButton } from "@mui/lab";
import {
  selectAddProductState,
  selectAllBrandsDataState,
  selectAllCategoryDataState,
} from "../selector";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { yupResolver } from "@hookform/resolvers/yup";
import { addNewProductSchema } from "../vaildations";
import { selectAllLocationDataState } from "../selector";
import { findLocation, newLocationAdd } from "../slice";
import { useRequestState } from "@app/hooks";

const AddProduct = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [isMapOpen, setMapOpen] = useState(false);
  const [currentLocationData, setCurrentLocationData] = useState<any>(null);

  const handleSelect = (id: number) => {
    setSelectedLocation(id);
    setValue("locationId", id);
  };

  const handleViewMap = (locationId: number) => {
    const locationData = locations.find((loc: any) => loc.id === locationId);
    setCurrentLocationData(locationData);
    setMapOpen(true);
  };

  const methods = useForm<any>({
    resolver: yupResolver(addNewProductSchema),
    defaultValues: {
      testDrive: false,
      locationId: null,
    },
  });

  const { handleSubmit, reset } = methods;
  const { setValue, getValues } = methods;
  const [images, setImages] = useState<File[]>([]);
  const [specs, setSpecs] = useState([""]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const testDrive = methods.watch("testDrive");

  const handleAddSpec = () => setSpecs([...specs, ""]);

  const handleRemoveSpec = (index: number) => {
    const updated = [...specs];
    updated.splice(index, 1);
    setSpecs(updated);
  };

  const locationState = useSelector(selectAllLocationDataState);
  const locations = locationState?.data ?? [];

  useEffect(() => {
    dispatch(findLocation());
  }, []);

  const handleSpecChange = (index: number, value: string) => {
    const updated = [...specs];
    updated[index] = value;
    setSpecs(updated);
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = [...images];
    const updatedPreviews = [...imagePreviews];
    URL.revokeObjectURL(updatedPreviews[index]);

    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);

    setImages(updatedImages);
    setImagePreviews(updatedPreviews);
    setValue("images", updatedImages);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(brands());
  }, [dispatch]);

  useEffect(() => {
    dispatch(category());
  }, [dispatch]);

  const handleSucess = useCallback(() => {
    imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
    reset();
    setImages([]);
    setImagePreviews([]);
    setSpecs([""]);
    setSelectedLocation(null);
    navigate(PATH_DASHBOARD.product);
  }, []);

  const { loading } = useRequestState({
    stateSelector: selectAddProductState,
    successMessageShown: true,
    errorShown: true,
    onSuccess: handleSucess,
  });

  const brandData = useSelector(selectAllBrandsDataState);
  const categoryData = useSelector(selectAllCategoryDataState);
  const onSubmit = (data: any) => {
    const formattedSpecs = specs
      .map((spec, idx) => {
        const key = spec.trim();
        const value = data?.specValue?.[idx]?.trim();

        if (key && value) {
          return {
            key,
            value,
          };
        }
        return null;
      })
      .filter(Boolean);

    const currentFormValues = getValues();
    const locationId = currentFormValues.locationId || selectedLocation;

    const finalFormData = {
      ...data,
      images: images,
      testDrive: data.testDrive,
      specifications: formattedSpecs,
      locationIds: locationId ? [locationId] : [],
    };

    dispatch(
      (addProduct as any)({
        formValues: finalFormData,
      })
    );
  };

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" sx={{ minHeight: "100vh", bgcolor: "#F5F5F5" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
            ></Stack>

            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <Box p={3}>
                    <Typography
                      fontWeight={600}
                      mb={2}
                      fontFamily={"Poppins"}
                      color="#000000"
                    >
                      {Strings.common.carDetails}
                    </Typography>
                    <Stack spacing={2}>
                      <input
                        type="hidden"
                        {...methods.register("locationId")}
                        value={selectedLocation || ""}
                      />

                      <RHFTextField name="name" label="Name" />
                      <RHFTextField
                        name="description"
                        label="Description"
                        multiline
                        rows={3}
                      />

                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                      >
                        <RHFTextField name="type" label="Type" />
                        <RHFTextField name="capacity" label="Capacity" />
                      </Stack>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                      >
                        <RHFTextField name="steering" label="Steering" />
                        <RHFTextField name="fuelType" label="Fuel Type" />
                      </Stack>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                      >
                        <RHFTextField
                          name="price"
                          label="Price"
                          type="number"
                        />
                        <RHFTextField name="year" label="Year" type="number" />
                        <RHFTextField
                          name="originalPrice"
                          label="Original Price"
                          type="number"
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                      >
                        <RHFSelect
                          name="brandId"
                          label="Brand"
                          fullWidth
                          variant="outlined"
                        >
                          {brandData?.data?.data?.map((brand: any) => (
                            <MenuItem key={brand.id} value={brand.id}>
                              {brand.name}
                            </MenuItem>
                          ))}
                        </RHFSelect>

                        <RHFSelect name="categoryId" label="Category">
                          {categoryData?.data?.data?.map((category: any) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </RHFSelect>

                        <RHFSelect name="vehicleType" label="Vehicle Type">
                          <MenuItem value="Two Wheeler">Two Wheeler</MenuItem>
                          <MenuItem value="Four Wheeler">Four Wheeler</MenuItem>
                        </RHFSelect>
                      </Stack>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                      >
                        <RHFTextField
                          name="stock"
                          label="Stock"
                          type="number"
                        />
                        <RHFRadioButton
                          name="testDrive"
                          label="Add to Test Drive"
                        />
                      </Stack>
                      {testDrive && (
                        <>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={3}
                          >
                            <Typography
                              fontWeight={600}
                              fontFamily={"Poppins"}
                              color="#000000"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {Strings.addProducts.locations}
                            </Typography>

                            <Button
                              onClick={(e) => {
                                setMapOpen(true);
                              }}
                              startIcon={<Add />}
                              sx={{
                                border: 1,
                                width: "auto",
                                height: 40,
                                fontFamily: "Public Sans",
                                color: "#505050",
                                fontWeight: 400,
                                textTransform: "none",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {Strings.addProducts.addNewLocation}
                            </Button>
                          </Stack>
                          <Divider />
                          <Spacing top={4} />
                          <Box
                            sx={{
                              border: "1px solid #eee",
                              borderRadius: 2,
                              p: { xs: 2, sm: 3 },
                              mt: 3,
                              backgroundColor: "#fff",
                            }}
                          >
                            <Typography
                              fontFamily={"Work Sans"}
                              fontWeight={600}
                              color="#454545"
                              gutterBottom
                            >
                              {Strings.addProducts.selectLocation}
                            </Typography>

                            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                              <Stack spacing={2}>
                                {locations.map((loc: any) => (
                                  <Paper
                                    key={loc.id}
                                    elevation={0}
                                    onClick={() => handleSelect(loc.id)}
                                    sx={{
                                      display: "flex",
                                      gap: 2,
                                      alignItems: "flex-start",
                                      p: 2,
                                      cursor: "pointer",
                                      backgroundColor: "#fafafa",
                                      borderRadius: 1,
                                      border:
                                        selectedLocation === loc.id
                                          ? "2px solid #1572D3"
                                          : "1px solid transparent",
                                    }}
                                  >
                                    <Radio
                                      checked={selectedLocation === loc.id}
                                      onChange={() => handleSelect(loc.id)}
                                      value={loc.id}
                                      sx={{
                                        mt: 0.5,
                                        color: theme.palette.primary.main,
                                        "&.Mui-checked": {
                                          color: theme.palette.primary.main,
                                        },
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        fontWeight="700"
                                        color="#595959"
                                        fontFamily={"Plus Jakarta Sans"}
                                      >
                                        {loc.name}
                                      </Typography>
                                      <Typography
                                        fontWeight="600"
                                        color="#595959"
                                        fontFamily={"Plus Jakarta Sans"}
                                      >
                                        {loc.address}
                                      </Typography>
                                      <Typography
                                        fontWeight="600"
                                        color="#595959"
                                        fontFamily={"Plus Jakarta Sans"}
                                      >
                                        {loc.city}, {loc.zipCode}
                                      </Typography>
                                      <Link
                                        href={`https://www.google.com/maps?q=${loc?.lat},${loc?.long}`}
                                        target="_blank"
                                        underline="hover"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewMap(loc.id);
                                        }}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          color: theme.palette.primary.main,
                                          mt: 0.5,
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        <Icon
                                          icon="mdi:map-marker"
                                          color={theme.palette.primary.main}
                                          width={16}
                                        />
                                        <Box
                                          ml={0.5}
                                          sx={{
                                            fontFamily: "Work Sans",
                                            fontWeight: 500,
                                            color: theme.palette.primary.main,
                                          }}
                                        >
                                          View Maps
                                        </Box>
                                      </Link>
                                    </Box>
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          </Box>
                        </>
                      )}
                      <Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={3}
                        >
                          <Typography
                            fontWeight={600}
                            fontFamily={"Poppins"}
                            color="#000000"
                          >
                            {Strings.addProducts.specification}
                          </Typography>
                          <Button
                            onClick={handleAddSpec}
                            startIcon={<Add />}
                            sx={{
                              border: 1,
                              width: "auto",
                              height: 40,
                              fontFamily: "Public Sans",
                              color: "#505050",
                              fontWeight: "400",
                              textTransform: "none",
                            }}
                          >
                            {Strings.addProducts.addMore}
                          </Button>
                        </Stack>
                        <Spacing top={4} />
                        <Divider />
                        {specs.map((spec, idx) => (
                          <Box key={idx} mt={2}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <RHFTextField
                                name={`specification.${idx}`}
                                fullWidth
                                label={`Specification ${idx + 1}`}
                                value={spec}
                                onChange={(e) =>
                                  handleSpecChange(idx, e.target.value)
                                }
                              />
                              {specs.length > 1 && (
                                <IconButton
                                  onClick={() => handleRemoveSpec(idx)}
                                  sx={{
                                    width: 70,
                                    height: 40,
                                    border: 1,
                                    borderRadius: 1,
                                    borderColor: "#919EAB52",
                                  }}
                                >
                                  <Remove />
                                </IconButton>
                              )}
                            </Stack>
                            <Box mt={2}>
                              <RHFTextField
                                name={`specValue.${idx}`}
                                label="Value"
                                multiline
                                rows={3}
                                fullWidth
                              />
                            </Box>
                          </Box>
                        ))}

                        <Spacing top={5} />
                      </Box>

                      <Box mt={4}>
                        <Typography fontWeight={600} mb={2}>
                          {Strings.addProducts.productImage}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          {imagePreviews.map((preview, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: "relative",
                                width: 100,
                                height: 100,
                              }}
                            >
                              <img
                                src={preview}
                                alt={`Preview ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                }}
                              />
                              <IconButton
                                onClick={() => handleRemoveImage(index)}
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  backgroundColor: "red",
                                  color: "white",
                                  width: 20,
                                  height: 20,
                                  "&:hover": {
                                    backgroundColor: "darkred",
                                  },
                                }}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                          <Box
                            onClick={() =>
                              document.getElementById("fileInput")?.click()
                            }
                            sx={{
                              width: 100,
                              height: 100,
                              border: "1px solid #1E91B6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                          >
                            <img
                              src={imageSource.camera}
                              alt="camera"
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        </Box>

                        <input
                          type="file"
                          id="fileInput"
                          accept="image/*"
                          multiple
                          hidden
                          onChange={(e) => {
                            const files = e.target.files;
                            if (!files) return;

                            const fileArray = Array.from(files);
                            const newImagePreviews = fileArray.map((file) =>
                              URL.createObjectURL(file)
                            );
                            setImages((prev) => [...prev, ...fileArray]);
                            setImagePreviews((prev) => [
                              ...prev,
                              ...newImagePreviews,
                            ]);
                            setValue("images", [...images, ...fileArray]);
                          }}
                        />
                      </Box>

                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={2}
                        sx={{ flex: 1, p: 2 }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => {
                            reset({
                              testDrive: false,
                              locationId: null,
                            });
                            setImages([]);
                            setImagePreviews([]);
                            setSpecs([""]);
                            setSelectedLocation(null);
                          }}
                          sx={{
                            borderRadius: 1,
                            height: 55,
                            color: "Cancel",
                            width: "50%",
                          }}
                        >
                          Cancel
                        </Button>
                        <LoadingButton
                          loading={loading}
                          variant="contained"
                          sx={{
                            borderRadius: 1,
                            height: 55,
                            color: "white",
                            width: "50%",
                          }}
                          onClick={handleSubmit(onSubmit)}
                        >
                          Submit
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </Box>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <MapModal
          open={isMapOpen}
          onClose={() => setMapOpen(false)}
          row={currentLocationData}
          onSave={(data) => {
            methods.setValue("locationName", `${data.name}, ${data.address}`);
            methods.setValue("lat", data.lat);
            methods.setValue("long", data.lng);
            methods.setValue("zipcode", data.zipCode);
            methods.setValue("city", data.city || "");
            methods.setValue("country", data.country || "");

            dispatch(
              (newLocationAdd as any)({
                address: data.address,
                name: data.name,
                city: data.city,
                country: data.country,
                zipCode: data.zipCode,
                lat: data.lat,
                long: data.lng,
              })
            );
          }}
        />
      </Stack>
    </FormProvider>
  );
};

export { AddProduct };
